import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { pushUrl, replaceUrl } from 'components/helpers/history';
import type { MarketplaceBrowseVariables } from 'components/marketplace/browse/marketplaceBrowse';
import { MarketplaceViewportAreaNameDocument } from 'components/marketplace/map/__generated__/viewport-area-name.zeus';
import {
  getViewportAreaName,
  getViewportAreaNameInput,
  getViewportCenter,
} from 'components/marketplace/map/viewportAreaName';
import { useRouter } from 'next/router';

import type { GeoPoint, MapLocation, MarketInfo } from '../map/types';
import type { SavedSearchFieldsFragment } from '../savedSearches/__generated__/saved-searches.zeus';
import { buildSaveSearchQuery, toPropertySearchQuery } from '../savedSearches/mapping';
import { canApplySavedSearchInPlace, pickPathname } from '../savedSearches/routing';
import { useSavedSearches } from '../savedSearches/useSavedSearches';
import type { ActiveMarket } from '../types';
import { DEFAULT_LISTING_SORT_SLUG } from './sortOptions';
import { type SearchStatus, useMarketplaceSearch } from './useMarketplaceSearch';
import {
  buildQueryParams,
  isUuid,
  normalizeSearchQuery,
  normalizeSearchVertices,
  parseQueryParamsFromSearch,
  SAVED_SEARCH_ID_PARAM,
  updateQueryParams,
} from './useSearchQueries';
import type { PropertySearchQuery } from './useSearchQueries';
import type { useSearchState } from './useSearchState';
import { buildPayloadFromQuery } from './useSearchState';

type SearchState = ReturnType<typeof useSearchState>;
type SearchFilterOverride = Omit<PropertySearchQuery, 'market' | 'vertices' | 'zoneIds'>;

interface UseMarketplaceSearchControllerArgs {
  initialVariables: MarketplaceBrowseVariables;
  initialSearchPathname: string;
  activeMarkets: ActiveMarket[];
  searchState: SearchState;
  marketInfo: MarketInfo;
  initialVertices?: GeoPoint[] | null;
  initialZoneIds?: string[] | null;
  initialViewportAreaName?: string | null;
  resolveViewportAreaName?: (center: GeoPoint, signal: AbortSignal) => Promise<string | null>;
  /** `ssid` from the URL: the saved search this page view was applied from. */
  initialSavedSearchId?: string | null;
}

const resolveViewportAreaNameFromZeus = async (
  center: GeoPoint,
  signal: AbortSignal,
): Promise<string | null> => {
  const data = await requestZeusFromBrowser(
    MarketplaceViewportAreaNameDocument,
    { input: getViewportAreaNameInput(center) },
    signal,
  );
  return getViewportAreaName(data);
};

export const useMarketplaceSearchController = ({
  initialVariables,
  initialSearchPathname,
  activeMarkets,
  searchState,
  marketInfo: initialMarketInfo,
  initialVertices,
  initialZoneIds,
  initialViewportAreaName,
  resolveViewportAreaName = resolveViewportAreaNameFromZeus,
  initialSavedSearchId,
}: UseMarketplaceSearchControllerArgs) => {
  const router = useRouter();
  const normalizedInitialVertices = normalizeSearchVertices(initialVertices);
  const {
    mapMarkers,
    listings,
    appliedSort,
    appliedAvailableSoon,
    appliedPage,
    isLoading,
    isLoadingMore,
    hasError,
    clearError,
    runSearch,
    loadNextPage,
  } = useMarketplaceSearch(initialVariables, searchState.get.sort);

  // Keeping "no polygon" as null lets save-search distinguish market-level
  // searches from user-drawn areas.
  const [drawnVertices, setDrawnVertices] = useState<GeoPoint[] | null>(normalizedInitialVertices);
  const [searchRoute, setSearchRoute] = useState({
    pathname: initialSearchPathname,
    marketInfo: initialMarketInfo,
  });
  const { pathname: searchPathname, marketInfo } = searchRoute;
  const getLocationRoute = useCallback(
    (location: MapLocation) => {
      // A city name alone can match a market in a different state.
      const market =
        location.zoneType === 'CITY'
          ? activeMarkets.find(
              (candidate) =>
                `${candidate.displayName}, ${candidate.displayState}` === location.label,
            )
          : undefined;
      return {
        pathname: market?.homesPath ?? `/homes/zid_${location.value}`,
        marketInfo: {
          ...initialMarketInfo,
          identifier: market?.identifier ?? null,
          name: market?.identifier ?? location.label,
          displayName: market?.displayName ?? location.label,
        },
      };
    },
    [activeMarkets, initialMarketInfo],
  );
  const [selectedLocation, setSelectedLocation] = useState<MapLocation>();
  const [viewportAreaName, setViewportAreaName] = useState<string | null>(() =>
    normalizedInitialVertices ? (initialViewportAreaName ?? 'this area') : null,
  );
  const locationLabel =
    selectedLocation?.label || viewportAreaName || marketInfo.displayName || 'you';
  const isMountedRef = useRef(true);
  const areaNameAbortControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
    };
  }, []);
  // Vertices win over zones (the same precedence used during SSR). When the page
  // hydrates with a viewport polygon, ignore route zone ids so client searches
  // and saves keep the polygon instead of silently expanding to the whole zone.
  const [routeZoneIds, setRouteZoneIds] = useState<string[] | null>(() => {
    if (normalizedInitialVertices) {
      return null;
    }
    return initialZoneIds?.length ? initialZoneIds : null;
  });

  // Resolve the active id against the shared cache so updates and deletions
  // propagate from every saved-search surface.
  const { savedSearches, hasLoaded: savedSearchesLoaded } = useSavedSearches();
  const [activeSavedSearchId, setActiveSavedSearchId] = useState<string | null>(
    initialSavedSearchId ?? null,
  );
  const activeSavedSearch = useMemo(
    () => savedSearches.find((saved) => saved.id === activeSavedSearchId) ?? null,
    [savedSearches, activeSavedSearchId],
  );

  const markActiveSavedSearch = useCallback((savedSearchId: string) => {
    if (!savedSearchId) {
      return;
    }
    setActiveSavedSearchId(savedSearchId);
    const params = new URLSearchParams(window.location.search);
    params.set(SAVED_SEARCH_ID_PARAM, savedSearchId);
    replaceUrl(`${window.location.pathname}?${params.toString()}`);
  }, []);

  const clearActiveSavedSearch = useCallback(() => {
    setActiveSavedSearchId(null);
    const params = new URLSearchParams(window.location.search);
    if (!params.has(SAVED_SEARCH_ID_PARAM)) {
      return;
    }
    params.delete(SAVED_SEARCH_ID_PARAM);
    replaceUrl(`${window.location.pathname}?${params.toString()}`);
  }, []);

  useEffect(() => {
    // Only reconcile against a genuinely loaded list; transient empties during
    // auth churn must not wipe the active reference.
    if (
      savedSearchesLoaded &&
      activeSavedSearchId &&
      !savedSearches.some((saved) => saved.id === activeSavedSearchId)
    ) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      clearActiveSavedSearch();
    }
  }, [savedSearchesLoaded, savedSearches, activeSavedSearchId, clearActiveSavedSearch]);

  // The location the next search runs against. `buildPayloadFromQuery` treats
  // these as mutually exclusive (vertices > zoneIds > market); zone selection
  // clears `vertices` so a chosen zone wins, otherwise the current map polygon
  // (or, when empty, the bare market) drives results.
  const activeZoneIds = useMemo(() => {
    if (selectedLocation?.value) {
      return [selectedLocation.value];
    }
    return routeZoneIds?.length ? routeZoneIds : null;
    // oxlint-disable-next-line react/preserve-manual-memoization -- react19-baseline: pre-existing, do not copy. Fix the compiler errors inside the callback first
  }, [routeZoneIds, selectedLocation?.value]);

  const currentSearchLocation: Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'> =
    useMemo(
      () => ({
        market: marketInfo.name,
        vertices: activeZoneIds?.length ? null : drawnVertices,
        zoneIds: activeZoneIds,
      }),
      [marketInfo.name, drawnVertices, activeZoneIds],
    );

  // Runs a search against the current location, optionally overriding filter
  // values being set in the same tick (the async filter setters wouldn't be
  // visible to `buildSearchPayload` yet). Backs both the quick and full filter
  // menus.
  const applyFilters = useCallback(
    async (filterOverride: SearchFilterOverride = {}): Promise<SearchStatus> => {
      const status = await runSearch(
        searchState.buildSearchPayload({ ...filterOverride, ...currentSearchLocation }),
        filterOverride.sort ?? searchState.get.sort,
      );
      if (status !== 'applied') {
        return status;
      }
      const canonicalQuery = { ...searchState.getCanonical(), ...filterOverride };
      if (activeZoneIds?.length === 1) {
        const route = selectedLocation ? getLocationRoute(selectedLocation) : searchRoute;
        const params = buildQueryParams({
          ...canonicalQuery,
          vertices: null,
          zoneIds: route.marketInfo.identifier ? activeZoneIds : null,
        });
        replaceUrl(`${route.pathname}?${params.toString()}`);
        setSearchRoute(route);
        return status;
      }
      updateQueryParams(canonicalQuery);
      return status;
    },
    [
      searchState,
      runSearch,
      currentSearchLocation,
      activeZoneIds,
      selectedLocation,
      getLocationRoute,
      searchRoute,
    ],
  );

  // Unlike a filter refinement, a page is its own destination: it lands as a new
  // history entry so Back returns to the page the visitor came from.
  const applyPage = useCallback(
    async (page: number): Promise<SearchStatus> => {
      const status = await runSearch(
        searchState.buildSearchPayload({ ...currentSearchLocation, page }),
        searchState.get.sort,
      );
      if (status !== 'applied') {
        return status;
      }
      // Canonical keys from the URL plus the new page. Passing them in is what
      // makes the alias-stripping loop fire, so `?minBeds=3` becomes
      // `?bedrooms=3` and the pushed address matches the anchor's href — while
      // seeding still preserves params neither path models, such as `ssid`.
      const params = buildQueryParams({
        ...parseQueryParamsFromSearch(window.location.search),
        page,
      });
      const query = params.toString();
      pushUrl(query ? `${window.location.pathname}?${query}` : window.location.pathname);
      return status;
    },
    [searchState, runSearch, currentSearchLocation],
  );

  // In-place searches do not update Next's route props. Block its competing
  // history fetch so it cannot hydrate stale props over the restored search.
  useEffect(() => {
    router.beforePopState(() => false);
    const handlePopState = () => {
      // Rebuilt from the entry's own URL rather than live filter state, which
      // has moved on: a Back that restores `bedrooms=3` must search with it, not
      // with whatever the filter panel holds now. Parsed the same way SSR parses
      // it, so a malformed or repeated param resolves identically on both paths.
      const query = parseQueryParamsFromSearch(window.location.search);
      const zoneIds =
        query.zoneIds ??
        (searchPathname.startsWith('/homes/zid_')
          ? [searchPathname.slice('/homes/zid_'.length)]
          : null);
      // A different location needs its SSR metadata and map camera, not just new listings.
      if (
        window.location.pathname !== searchPathname ||
        JSON.stringify(normalizeSearchVertices(query.vertices)) !== JSON.stringify(drawnVertices) ||
        (!drawnVertices && JSON.stringify(zoneIds) !== JSON.stringify(activeZoneIds))
      ) {
        window.location.reload();
        return;
      }
      void (async () => {
        const status = await runSearch(
          buildPayloadFromQuery({ ...query, ...currentSearchLocation }, currentSearchLocation),
          query.sort ?? DEFAULT_LISTING_SORT_SLUG,
        );
        if (status === 'applied') {
          searchState.applyCanonical(query);
          searchState.set.setSort(query.sort ?? DEFAULT_LISTING_SORT_SLUG);
          setActiveSavedSearchId(
            new URLSearchParams(window.location.search).get(SAVED_SEARCH_ID_PARAM),
          );
        }
      })();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      router.beforePopState(() => true);
    };
  }, [
    router,
    runSearch,
    searchState,
    currentSearchLocation,
    searchPathname,
    drawnVertices,
    activeZoneIds,
  ]);

  // Preserve an explicit dropdown selection for retry, but commit its route
  // only after the corresponding results apply.
  const applyZoneSearch = useCallback(
    async (location: MapLocation) => {
      const zoneId = location.value;
      if (!isUuid(zoneId)) {
        return;
      }

      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
      setViewportAreaName(null);
      setSelectedLocation(location);
      setRouteZoneIds([zoneId]);
      setDrawnVertices(null);

      const status = await runSearch(
        searchState.buildSearchPayload({ zoneIds: [zoneId], vertices: null }),
        searchState.get.sort,
      );
      if (status !== 'applied') {
        return;
      }

      const route = getLocationRoute(location);
      const params = buildQueryParams(
        { ...searchState.getCanonical(), zoneIds: route.marketInfo.identifier ? [zoneId] : null },
        { seedFromUrl: false },
      );
      pushUrl(`${route.pathname}?${params.toString()}`);
      setSearchRoute(route);
      setActiveSavedSearchId(null);
    },
    [searchState, runSearch, getLocationRoute],
  );

  const applyViewport = useCallback(
    async (coordinates: GeoPoint[], location?: MapLocation) => {
      const vertices = normalizeSearchVertices(coordinates);
      const center = vertices ? getViewportCenter(vertices) : null;
      if (!vertices || !center || (location && !isUuid(location.value))) {
        return;
      }
      areaNameAbortControllerRef.current?.abort();
      const areaNameAbortController = new AbortController();
      areaNameAbortControllerRef.current = areaNameAbortController;
      const areaNamePromise = resolveViewportAreaName(center, areaNameAbortController.signal).catch(
        (error: unknown) => {
          if (!areaNameAbortController.signal.aborted) {
            Sentry.captureException(error, {
              tags: { feature: 'marketplace-browse', operation: 'viewportAreaName' },
            });
          }
          return null;
        },
      );
      const status = await runSearch(
        searchState.buildSearchPayload({ vertices }),
        searchState.get.sort,
      );
      // Commit the viewport only with its results so URL, list, and subsequent
      // saved-search/load-more actions stay coherent.
      if (status !== 'applied' || !isMountedRef.current) {
        areaNameAbortController.abort();
        if (areaNameAbortControllerRef.current === areaNameAbortController) {
          areaNameAbortControllerRef.current = null;
        }
        return;
      }

      const route = location ? getLocationRoute(location) : searchRoute;
      const params = buildQueryParams(
        { ...searchState.getCanonical(), vertices, page: null, market: null },
        { seedFromUrl: !location },
      );
      const url = `${route.pathname}?${params.toString()}`;
      if (location) {
        pushUrl(url);
        setActiveSavedSearchId(null);
      } else {
        replaceUrl(url);
      }
      setSearchRoute(route);

      setDrawnVertices(vertices);
      setViewportAreaName(location?.label ?? locationLabel);
      setSelectedLocation(undefined);
      setRouteZoneIds(null);

      const areaName = await areaNamePromise;
      if (
        areaNameAbortControllerRef.current === areaNameAbortController &&
        !areaNameAbortController.signal.aborted
      ) {
        setViewportAreaName(areaName ?? 'this area');
        areaNameAbortControllerRef.current = null;
      }
    },
    [searchState, runSearch, searchRoute, getLocationRoute, resolveViewportAreaName, locationLabel],
  );

  const loadMore = useCallback(async (): Promise<boolean> => {
    if (listings?.nextPage == null) {
      return false;
    }
    return loadNextPage();
  }, [listings?.nextPage, loadNextPage]);

  // Zone, polygon, and cross-market searches need fresh SSR chrome; only a
  // same-market market-level search can apply in place safely.
  const applySavedSearch = useCallback(
    async (savedSearch: SavedSearchFieldsFragment) => {
      // Saved searches store filters and location only, so the active sort
      // carries over instead of snapping back to the default.
      const query = normalizeSearchQuery({
        ...toPropertySearchQuery(savedSearch),
        sort: searchState.get.sort,
      });

      if (!canApplySavedSearchInPlace(savedSearch, marketInfo.identifier)) {
        const params = buildQueryParams(query, { seedFromUrl: false });
        params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
        window.location.assign(`${pickPathname(savedSearch)}?${params.toString()}`);
        return;
      }

      // State setters are not visible to `buildSearchPayload` in this tick.
      const status = await runSearch(
        buildPayloadFromQuery(query, { market: marketInfo.name }),
        // normalizeSearchQuery nulls the default slug, so fall back to state.
        query.sort ?? searchState.get.sort,
      );
      if (status !== 'applied') {
        return;
      }

      searchState.applyCanonical(query);
      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
      setViewportAreaName(null);
      setSelectedLocation(undefined);
      // Only market-level searches apply in place, so the applied state has no
      // drawn polygon.
      setDrawnVertices(null);
      setRouteZoneIds(null);
      setActiveSavedSearchId(savedSearch.id);

      // A zone pathname would re-SSR the zone search on reload/share/back.
      const params = buildQueryParams(query, { seedFromUrl: false });
      params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
      const pathname = `/homes/${marketInfo.identifier}`;
      replaceUrl(`${pathname}?${params.toString()}`);
      setSearchRoute({ pathname, marketInfo });
    },
    [marketInfo, searchState, runSearch],
  );

  // The canonical query handed to the save-search button. `getCanonical()`
  // omits location, so the active market/zone/polygon is merged back in here.
  // We persist the routing slug (`identifier`), never the display `name`, so a
  // zone page's label isn't written into the saved search and later routed to a
  // non-existent `/homes/<label>`. Only a genuinely drawn polygon is persisted
  // as vertices — with none, the search saves as a clean market-level record.
  const getSaveSearchQuery = useCallback(
    (): PropertySearchQuery =>
      buildSaveSearchQuery({
        canonical: searchState.getCanonical(),
        marketIdentifier: marketInfo.identifier,
        selectedZoneId: selectedLocation?.value,
        routeZoneIds,
        vertices: activeZoneIds?.length ? null : drawnVertices,
      }),
    [
      searchState,
      marketInfo.identifier,
      selectedLocation?.value,
      routeZoneIds,
      activeZoneIds,
      drawnVertices,
    ],
  );

  return {
    mapMarkers,
    listings,
    appliedSort,
    appliedAvailableSoon,
    appliedPage,
    isLoading,
    isLoadingMore,
    hasError,
    clearError,
    selectedLocation,
    setSelectedLocation,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    applyPage,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
    locationLabel,
    searchPathname,
    marketInfo,
    searchLocation: currentSearchLocation,
  };
};
